<template>
    <div class="people">
        <OnboardHint name="upgrade-people" v-if="showLimitHint" :app="app">
            <template slot="content">
                <h4><i>😬</i> You've hit the people-limit!</h4>
                <p>The free version of this plug-in only includes {{peopleLimit}} people.</p>
                <a href="https://www.drip-funnels.com/try/" target="_blank" class="button">
                    Try Unlimited FREE for 14 Days
                </a> &nbsp;
                <a href="javascript:;" @click="app.showActivationModal" class="link">Enter Activation Code</a>
            </template>
        </OnboardHint>

        <OnboardHint name="people" v-else :app="app">
            <template slot="content">
                <h4><i>🎉</i> Thanks for installing Drip Funnels!</h4>
                <p>The "People" page lists all of your contacts, along with their information.</p>
                <p>From this page, it's <em>easy</em> to manually start <strong>person.name</strong> on the (1-email) <strong>Welcome</strong> funnel:</p>
                <ul class="list">
                    <li>Check the box by your name in the list below</li>
                    <li>Click on the Actions dropdown</li>
                    <li>Choose "Add to Funnel"</li>
                </ul>
                <p>When you're done, head over to the <router-link to="/funnels">Funnels</router-link> page to discover how the Welcome funnel works.</p>
            </template>
            <template slot="button">
                Thanks! <span class="hint inline">and hide this message</span>
            </template>
        </OnboardHint>

        <div class="meta-nav">
            <input type="text" v-model="q" placeholder="Search..." class="search">

            <ul>
                <li :class="showLimitHint ? 'disabled-nevermind' : ''">
                    <a @click="showLimitHint ? app.showActivationModal('Add More People') : app.showAddPeopleModal()" href="javascript:;">
                        + Add People
                    </a>
                </li>

                <li v-if="app.people.length > 5">
                    <a @click="showSegments = !showSegments" v-on-clickaway="hideSegments" href="javascript:;">
                        <span v-if="segment">{{segment.name}} ({{people.length}} / {{app.people.length}})</span>
                        <span v-else>Everyone ({{app.people.length}})</span>
                        <font-awesome-icon :icon="showSegments ? 'chevron-up' : 'chevron-down'" />
                    </a>

                    <ul v-if="showSegments">
                        <li>
                            <a v-on:click="selectSegment()" href="javascript:;">
                                Everyone
                            </a>
                        </li>

                        <li v-for="s in app.segments" :key="'segment-' + s.id">
                            <a v-on:click="selectSegment(s)" href="javascript:;">
                                {{s.name}}
                            </a>
                        </li>
                    </ul>
                </li>

                <li :class="app.selected.length ? '' : 'disabled'">
                    <a @click="app.selected.length ? showActions = !showActions : null" href="javascript:;" v-on-clickaway="hideActions">
                        Actions ({{app.selected.length}})
                        <font-awesome-icon :icon="showActions ? 'chevron-up' : 'chevron-down'" />
                    </a>

                    <ul v-if="showActions">
                        <li>
                            <a @click="app.showAddToCampaignModal()" href="javascript:;">
                                Add to Funnel
                            </a>
                        </li>
                        <li>
                            <a @click="app.selected = []" href="javascript:;">
                                Clear Selection
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>

        <div class="table-wrapper" :style="'height:'+ tableHeight">
            <ScrollingTable class="table">
                <template slot="thead">
                    <tr>
                        <th class="no-padding has-checkbox">
                            <label class="checkbox-label">
                                <input type="checkbox" :checked="allSelected" v-on:change="selectAll()">
                            </label>
                        </th>
                        <!-- <th class="narrow align-center">
                            <a>ID</a>
                        </th> -->
                        <th v-for="col in app.columns" :key="'col-' + col.name">
                            <a v-on:click="changeSort(col)" href="javascript:;">
                                {{col.name}}
                                <span v-if="col === sortColumn">
                                    <font-awesome-icon v-if="sortAsc" icon="chevron-down" />
                                    <font-awesome-icon v-else icon="chevron-up" />
                                </span>
                            </a>
                        </th>
                        <th class="no-padding has-checkbox"></th>
                    </tr>
                </template>
                <template slot="tbody">
                    <tr v-for="person in people" :class="isSelected(person) ? 'selected' : ''" :key="'person-' + person.id">
                        <td class="has-checkbox">
                            <label class="checkbox-label">
                                <input type="checkbox" v-model="app.selected" :value="person">
                            </label>
                        </td>
                        <!-- <td class="narrow align-center">
                            <a>{{person.id}}</a>
                        </td> -->
                        <td v-for="col in app.columns" :key="'person-col-' + col.name">
                            <label v-if="col.type === 'checkbox'" class="checkbox-label">
                                <input type="checkbox" :checked="app.checked(person[col.name])" @change="person[col.name] = !app.checked(person[col.name]); app.savePerson(person)">
                            </label>
                            <input v-else :type="col.type" :value="person[col.name]" @change="update(person, col.name, $event.target.value)">
                        </td>
                        <td class="has-checkbox align-center">
                            <a v-on:click="removePerson(person)" href="javascript:;">
                                <font-awesome-icon icon="trash" />
                            </a>
                        </td>
                    </tr>
                </template>
            </ScrollingTable>
        </div>
    </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import ScrollingTable         from '@/pods/scrolling-table/component.vue';
import OnboardHint            from '@/pods/onboard-hint/component.vue';

function APPLY_SEGMENT(segment) {
    var filters = (segment && segment.data) ? segment.data.filters : [],
        none = !filters.length,
        filter;

    return function(person) {
        if (none) return;

        for (var i = filters.length - 1; i >= 0; i--) {
            filter = filters[i];

            if (filter.matcher === '===') {
                if (person[filter.key] + '' !== filter.value) {
                    return false;
                }
            } else if (filter.matcher === '!==') {
                if (person[filter.key] + '' === filter.value) {
                    return false;
                }
            }
        }

        return true;
    };
}

export default {
    name: 'people',
    mixins: [ clickaway ],
    components: {
        OnboardHint,
        ScrollingTable
    },
    props: ['app'],
    data() {
        return {
            q: '',
            tableHeight: '500px',
            peopleLimit: 100,
            segment: null,
            loading: true,
            sortColumn: this.app.columns[0] || { name: 'id' },
            sortAsc: true,
            showSegments: false,
            showActions: false
        };
    },
    watch: {
        segment() {
            this.selectNone();
        },
    },
    // mounted() {
    //     var _ = this,
    //         parent = document.getElementById('app').parentNode;

    //     window.onresize = function resizeWindow() {
    //         _.tableHeight = (parent.clientHeight - 215) + 'px';
    //     };

    //     window.onresize();
    // },
    computed: {
        allSelected() {
            var _ = this;
            return _.app.selected.length >= _.people.length;
        },
        people() {
            var _ = this,
                q = _.q.toLowerCase(),
                segment = _.segment,
                people = _.app.people,
                column = _.sortColumn.name;

            if (segment) {
                people = people.filter(APPLY_SEGMENT(segment));
            }

            if (q) {
                people = people.filter(function(person) {
                    return JSON.stringify(person).toLowerCase().indexOf(q) !== -1;
                });
            }

            return people.sort(function(a, b) {
                if ( a[column] && !b[column]) return  _.sortAsc ? -1 : 1;
                if (!a[column] &&  b[column]) return !_.sortAsc ? -1 : 1;
                if ( a[column] <   b[column]) return  _.sortAsc ? -1 : 1;
                if ( a[column] >   b[column]) return !_.sortAsc ? -1 : 1;
                return 0;
            });
        },
        atPeopleLimit() {
            var _ = this;
            return _.app.people.length >= _.peopleLimit;
        },
        showLimitHint() {
            var _ = this;
            return _.atPeopleLimit && !_.app.subscription;
        }
    },
    methods: {
        isSelected(person) {
            return person && this.app.selected.indexOf(person) !== -1;
        },
        selectNone() {
            var _ = this;
            _.app.selected.splice(0, _.app.selected.length);
        },
        selectAll() {
            var _ = this;

            if (_.app.selected.length >= _.people.length) {
                _.selectNone();
            } else {
                for (var i = _.people.length - 1; i >= 0; i--) {
                    if (_.app.selected.indexOf(_.people[i]) === -1) {
                        _.app.selected.push(_.people[i]);
                    }
                }
            }
        },
        select(person) {
            var _ = this,
                index = _.app.selected.indexOf(person);

            if (index !== -1) {
                _.app.selected.splice(index, 1);
            } else {
                _.app.selected.push(person);
            }
        },
        selectSegment(segment) {
            var _ = this;

            _.segment = segment;
            _.app.selected = [];
            _.showSegments = false;
        },
        changeSort(col) {
            var _ = this;

            if (_.sortColumn === col) {
                _.sortAsc = !_.sortAsc;
                _.$forceUpdate();
            } else {
                _.sortAsc = true;
                _.sortColumn = col;
            }
        },

        removePerson(person) {
            var _ = this;

            if (confirm('Are you sure you want to delete this person?')) {
                _.app.people.splice(_.app.people.indexOf(person), 1);

                _.app.requester.request({
                    controller: 'people',
                    action: 'delete',
                    id: person.id
                });
            }
        },

        hideSegments() {
            var _ = this;

            _.showSegments = false;
        },

        hideActions() {
            var _ = this;

            _.showActions = false;
        },

        update(person, key, val) {
            var _ = this;

            person[key] = val;

            _.app.savePerson(person);
        }
    }
};
</script>
